import { useEffect, useState, useCallback } from "react";
import {
  fetchMovements, fetchValidation, deleteMovement, movementsCsvUrl,
} from "@/lib/api";
import MovementFormSheet from "@/components/MovementFormSheet";
import MovementCard from "@/components/MovementCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { Plus, Search, X, Download } from "lucide-react";

const PAGE = 24;

export default function Movements() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({});
  const [qLive, setQLive] = useState("");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ mvmnt_mfg: "", movement_type: "", size: "", protection: "" });
  const [hideEmpty, setHideEmpty] = useState(false);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip, limit: PAGE };
    if (q) params.q = q;
    if (hideEmpty) params.has_specs = true;
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    fetchMovements(params)
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [skip, q, filters, hideEmpty]);

  useEffect(() => { fetchValidation().then(setValidation); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // reset page when hideEmpty changes
    setSkip(0);
  }, [hideEmpty]);

  useEffect(() => {
    const t = setTimeout(() => { setQ(qLive); setSkip(0); }, 250);
    return () => clearTimeout(t);
  }, [qLive]);

  const setFilter = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v === "__all__" ? "" : v }));
    setSkip(0);
  };

  const clearFilters = () => {
    setFilters({ mvmnt_mfg: "", movement_type: "", size: "", protection: "" });
    setQLive("");
    setSkip(0);
  };

  const onSaved = () => {
    setSheetOpen(false);
    setEditing(null);
    load();
    fetchValidation().then(setValidation);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMovement(confirmDelete.id);
      toast.success(`Removed ${confirmDelete.movement_id}`);
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (q ? 1 : 0);

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-[1600px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Reference Library</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A0604] leading-none">Movements</h1>
          <div className="text-sm text-[#3A2A10] mt-3 font-mono-data">
            {loading ? "…" : `${total.toLocaleString()} calibers catalogued`}
          </div>
        </div>
        <Button
          onClick={() => { setEditing(null); setSheetOpen(true); }}
          className="h-9 px-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm"
          data-testid="add-movement-button"
        >
          <Plus size={14} className="mr-1.5" /> Add Movement
        </Button>
      </header>

      <div className="flex items-center gap-2 mb-4 -mt-2 flex-wrap">
        <a
          href={movementsCsvUrl()}
          className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-wider border border-[#8B6A1F]/45 hover:border-[#8B6A1F]/60 text-[#0A0604] hover:text-[#0A0604] transition-colors"
          data-testid="export-movements-csv"
        >
          <Download size={14} /> Export CSV
        </a>
        <button
          onClick={() => setHideEmpty((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs uppercase tracking-wider border transition-colors ${
            hideEmpty
              ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#3A2A10]"
              : "border-[#8B6A1F]/45 hover:border-[#8B6A1F]/60 text-[#3A2A10] hover:text-[#0A0604]"
          }`}
          data-testid="toggle-hide-empty"
        >
          {hideEmpty ? "Showing filled only" : "Show filled only"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A2A10]" />
          <Input
            value={qLive}
            onChange={(e) => setQLive(e.target.value)}
            placeholder="Search caliber, maker, notes…"
            className="pl-9 h-10 bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm focus-visible:ring-[#D4AF37]/40"
            data-testid="movements-search-input"
          />
        </div>
        <FilterSelect value={filters.mvmnt_mfg} onChange={(v) => setFilter("mvmnt_mfg", v)} options={validation["Mvmnt MFG"]} placeholder="Manufacturer" testid="filter-mvmnt-mfg" />
        <FilterSelect value={filters.movement_type} onChange={(v) => setFilter("movement_type", v)} options={validation["Movement Type"]} placeholder="Type" testid="filter-movement-type" />
        <FilterSelect value={filters.size} onChange={(v) => setFilter("size", v)} options={validation["Size"]} placeholder="Size" testid="filter-mvmnt-size" />
        <FilterSelect value={filters.protection} onChange={(v) => setFilter("protection", v)} options={validation["Shock Systems"]} placeholder="Protection" testid="filter-protection" />
      </div>
      {activeFilterCount > 0 && (
        <button
          onClick={clearFilters}
          className="inline-flex items-center gap-1.5 text-xs text-[#3A2A10] hover:text-[#3A2A10] transition-colors mb-4"
          data-testid="clear-mvmnt-filters"
        >
          <X size={12} /> Clear ({activeFilterCount})
        </button>
      )}

      {!loading && items.length === 0 && (
        <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] py-20 text-center" data-testid="movements-empty">
          <div className="font-serif-display text-3xl text-[#0A0604] mb-2">No movements yet</div>
          <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">Start cataloguing your calibers — add one to begin.</div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#3A2A10] py-16 text-center" data-testid="movements-loading">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="movements-grid">
          {items.map((m) => (
            <MovementCard
              key={m.id}
              movement={m}
              onEdit={() => { setEditing(m); setSheetOpen(true); }}
              onDelete={() => setConfirmDelete(m)}
            />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-8">
        <div className="text-xs text-[#3A2A10] font-mono-data">
          {total === 0 ? "0" : `${skip + 1}–${Math.min(skip + PAGE, total)}`} / {total.toLocaleString()}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => setSkip(Math.max(0, skip - PAGE))}
            disabled={skip === 0}
            className="h-8 border-[#8B6A1F]/45 bg-transparent hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm"
            data-testid="mvmnt-page-prev"
          >Previous</Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setSkip(skip + PAGE)}
            disabled={skip + PAGE >= total}
            className="h-8 border-[#8B6A1F]/45 bg-transparent hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm"
            data-testid="mvmnt-page-next"
          >Next</Button>
        </div>
      </div>

      <MovementFormSheet
        open={sheetOpen}
        onOpenChange={(o) => { setSheetOpen(o); if (!o) setEditing(null); }}
        movement={editing}
        validation={validation}
        onSaved={onSaved}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Remove movement?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              Delete <span className="text-[#0A0604]">{confirmDelete?.movement_id}</span> from your reference library?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm" data-testid="mvmnt-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="mvmnt-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterSelect({ value, onChange, options = [], placeholder, testid }) {
  return (
    <Select value={value || "__all__"} onValueChange={onChange}>
      <SelectTrigger
        className="h-10 bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm focus:ring-[#D4AF37]/40"
        data-testid={testid}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm combobox-scroll"
        style={{
          maxHeight: "min(320px, var(--radix-select-content-available-height, 320px))",
        }}
      >
        <SelectItem value="__all__">All {placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={String(o)} value={String(o)}>{String(o)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
